import { action, makeObservable, observable } from 'mobx';
import { getGameCanvasStandards } from '../helpers/gameCanvasHelper';
import { betStatuses, betTypes } from '../enums';
import {
  BetResponseCode,
  betStore,
  deepCopy,
  gameConfigStore,
  notificationStore,
  parseNumberByPrecision,
  userInfoStore,
} from '@pascal/pascal-core';
import { doBetsStore } from './doBetsStore';

const getRoundsPointsCollection = (roundsInfo) => {
  const roundsPointsCollection = [];

  roundsInfo.dots?.forEach((round) => (round.dots ? roundsPointsCollection.push(...round.dots) : null));

  return roundsPointsCollection;
};

const getRoundsResultCollection = (roundsInfo) => {
  const roundsResultCollection = {};

  roundsInfo.dots.forEach((round) => {
    roundsResultCollection[round.roundId] = round.result;
  });

  return roundsResultCollection;
};

const getRoundsBetsCollection = (roundsInfo) => {
  const roundsBetsCollection = {};

  roundsInfo.dots?.forEach((round) => {
    roundsBetsCollection[round.roundId] = [];

    round?.bets?.forEach((bets) => {
      bets?.additionalParams?.markets?.forEach((bet) => {
        if (bet.marketType === betTypes.range) {
          roundsBetsCollection[round.roundId].push(bet);
        }
      });
    });
  });
  return roundsBetsCollection;
};

const getChartPointsInCanvasCoords = (roundsPoints, concentrationDiff) =>
  roundsPoints.map((point) => point * 10 - concentrationDiff);

const getConcentrationDiff = (concentrationPoint) => {
  const { canvas } = getGameCanvasStandards();
  return concentrationPoint * 10 - canvas.height / 2;
};

const createDemoBet = (bet) => ({
  roundId: +bet.roundId,
  date: new Date().toISOString(),
  winAmount: bet.winAmount,
  additionalParams: { markets: [{ ...bet }] },
});

const mergeOldBetsToNewBets = (oldBetHistory, betsHistory, roundResults) => {
  for (const roundId in betsHistory) {
    if (oldBetHistory[roundId]) {
      betsHistory[roundId] = oldBetHistory[roundId];
    } else {
      betsHistory[roundId] = [];
    }
  }

  for (const roundId in betsHistory) {
    if (roundResults[roundId] && betsHistory[roundId]?.length) {
      const result = roundResults[roundId];
      const resultRange = {};

      if (result % 10 <= 4) {
        resultRange.start = Math.floor(result / 10) * 10;
        resultRange.end = Math.floor(result / 10) * 10 + 4;
      } else {
        resultRange.start = Math.floor(result / 10) * 10 + 5;
        resultRange.end = Math.floor(result / 10) * 10 + 9;
      }

      betsHistory[roundId] = betsHistory[roundId].map((bet) => {
        if (bet.point >= resultRange.start && bet.point <= resultRange.end) {
          bet.status = betStatuses.win;
          bet.winAmount = parseNumberByPrecision(bet.amount, bet.odd);
        } else {
          bet.status = betStatuses.lose;
          bet.winAmount = 0;
        }
        return bet;
      });
    }
  }

  return betsHistory;
};

const initialUnderOverBets = { [betTypes.under]: null, [betTypes.over]: null };
const initialWiningBets = { [betTypes.under]: null, [betTypes.over]: null, [betTypes.range]: null };

class RoundsInfoStore {
  roundsInfo = [];
  currentRoundId = 0;
  isTabVisibility = true;
  concentrationPoint = 0;
  roundsPoints = [];
  chartPoints = [];
  animatingPoints = [];
  betsHistory = {};
  roundResults = {};
  realBetsHistory = {};
  chartStartPoint = 0;
  concentrationDiff = 0;
  underOverBets = initialUnderOverBets;
  winingBets = initialWiningBets;
  lastResult = 0;
  lastWinBets = [];
  higher = '0HIGHER';
  lower = '0LOWER';
  presentPoint = {
    x: 0,
    y: 0,
    point: 0,
    diff: 0,
  };

  constructor() {
    makeObservable(this, {
      roundsInfo: observable,
      currentRoundId: observable,
      isTabVisibility: observable,
      roundsPoints: observable,
      chartPoints: observable,
      concentrationPoint: observable,
      presentPoint: observable,
      animatingPoints: observable,
      chartStartPoint: observable,
      realBetsHistory: observable.deep,
      concentrationDiff: observable,
      underOverBets: observable,
      winingBets: observable,
      lastResult: observable,
      lastWinBets: observable,
      lower: observable,
      higher: observable,
      setRoundsInfo: action.bound,
      growChartBy: action.bound,
      shiftAnimationPoint: action.bound,
      shiftRound: action.bound,
      setPresentPoint: action.bound,
      setConcentrationPoint: action.bound,
      handleNewBets: action.bound,
      addNewRoundsInBetsHistory: action.bound,
      checkWiningBet: action.bound,
      cleanUnderOverBets: action.bound,
      getPenultimateResult: action.bound,
      setCurrentRoundId: action.bound,
      setIsTabVisibility: action.bound,
      doBetForDemo: action.bound,
      setLastResult: action.bound,
      setLastWinBets: action.bound,
      mapRealBetsToBetsHistory: action.bound,
      pushMyDemoBetsInLiveBets: action.bound,
      updateDemoBetHistory: action.bound,
      clearBetsHistory: action.bound,
      updateDemoBets: action.bound,
      addRoundResult: action.bound,
      clearBetsState: action.bound,
      setHigher: action.bound,
      setLower: action.bound,
    });
  }

  setHigher(text) {
    this.higher = text;
  }

  setLower(text) {
    this.lower = text;
  }

  clearBetsHistory() {
    this.betsHistory = {};
  }

  clearBetsState() {
    this.clearBetsHistory();
    this.underOverBets = initialUnderOverBets;
    userInfoStore.setDemoInitialBalance();
    this.winingBets = initialWiningBets;
  }

  setRoundsInfo(data) {
    // const { isDemo } = applicationStore;
    this.setCurrentRoundId(data.dots[7].roundId);
    this.roundsInfo = data;
    this.chartStartPoint = -150;
    this.animatingPoints = [];
    this.roundResults = getRoundsResultCollection(data);
    this.roundsPoints = getRoundsPointsCollection(data);
    this.realBetsHistory = getRoundsBetsCollection(data);
    this.betsHistory = getRoundsBetsCollection(data);
    this.concentrationPoint = this.roundsPoints[this.roundsPoints.length - 1];
    this.concentrationDiff = getConcentrationDiff(this.concentrationPoint);
    this.chartPoints = getChartPointsInCanvasCoords(this.roundsPoints, this.concentrationDiff);
    this.setLastResult(this.roundsPoints[this.roundsPoints.length - 1]);
    doBetsStore.setBetBlocks(this.roundsInfo.betBlocks);
    // doBetsStore.betBlocks.length === 0 && doBetsStore.setBetBlocks(this.roundsInfo.betBlocks);

    if (this.roundsInfo.dots[7].dots) {
      //shift round
      delete this.betsHistory[Object.keys(this.betsHistory)[0]];
      this.roundsPoints.splice(0, 5);
      this.chartPoints.splice(0, 5);
    }

    this.presentPoint.x = this.chartStartPoint + this.chartPoints.length * 20;
    this.presentPoint.y = this.chartPoints[this.chartPoints.length - 1];
    this.presentPoint.point = this.roundsPoints[this.roundsPoints.length - 1];
  }

  updateDemoBets(roundsInfo) {
    const newBetHistory = getRoundsBetsCollection(roundsInfo);
    return mergeOldBetsToNewBets(this.betsHistory, newBetHistory, this.roundResults);
  }

  mapRealBetsToBetsHistory() {
    return mergeOldBetsToNewBets(this.realBetsHistory, this.betsHistory, this.roundResults);
  }

  getPenultimateResult() {
    return this.roundsPoints[this.roundsPoints.length - 1];
  }

  addNewRoundsInBetsHistory(data) {
    for (const roundId in data) {
      if (!this.betsHistory[roundId]) {
        this.betsHistory[roundId] = [];
      }
    }
    this.winingBets = initialWiningBets;
  }

  handleNewBets(data) {
    data.betData.forEach((roundBets) => {
      roundBets.markets.forEach((bet) => {
        if (bet.marketType === betTypes.range) {
          this.betsHistory[roundBets.roundId].push(bet);
        } else {
          this.underOverBets[bet.marketType] = bet;
        }
      });
    });
  }

  setCurrentRoundId(roundId) {
    this.currentRoundId = roundId;
  }

  cleanUnderOverBets() {
    this.underOverBets = initialUnderOverBets;
  }

  checkWiningBet() {
    //const { userId } = userInfoStore;
    // const { isDemo } = applicationStore;
    const lastResult = this.presentPoint.point;
    const resultRange = {};
    const penultimateResult = this.underOverBets[betTypes.over]
      ? this.underOverBets[betTypes.over].point
      : this.underOverBets[betTypes.under]
      ? this.underOverBets[betTypes.under].point
      : 0;
    const lastResultsDiffs = lastResult - penultimateResult;
    const isResultOvered = lastResultsDiffs > 0;
    const isResultUnder = lastResultsDiffs < 0;
    let winingBalance = 0;

    if (lastResult % 10 <= 4) {
      resultRange.start = Math.floor(lastResult / 10) * 10;
      resultRange.end = Math.floor(lastResult / 10) * 10 + 4;
    } else {
      resultRange.start = Math.floor(lastResult / 10) * 10 + 5;
      resultRange.end = Math.floor(lastResult / 10) * 10 + 9;
    }

    const copyBetHistory = deepCopy(this.betsHistory);
    if (Object.keys(copyBetHistory) && Object.keys(copyBetHistory)[6]) {
      copyBetHistory[Object.keys(copyBetHistory)[6]] = Object.values(copyBetHistory)[6].map((bet) => {
        if (bet.point >= resultRange.start && bet.point <= resultRange.end) {
          bet.status = betStatuses.win;
          bet.winAmount = parseNumberByPrecision(bet.amount, bet.odd);
          winingBalance += bet.winAmount;
          this.winingBets[betTypes.range] = { ...bet };
        } else {
          bet.status = betStatuses.lose;
          bet.winAmount = 0;
        }
        this.betsHistory = copyBetHistory;

        // if(isDemo){
        //   betStore.updateLiveBets({
        //     userId: undefined,
        //     marketType: betTypes.range,
        //     winAmount: bet.winAmount,
        //     roundId: +bet.roundId,
        //     status: bet.status,
        //     amount: bet.amount,
        //     point: bet.point,
        //     odd: bet.odd,
        //     isDemoBet: true
        //   });
        // }

        return bet;
      });
    }

    if (this.underOverBets[betTypes.over]) {
      this.underOverBets[betTypes.over].status = isResultOvered ? betStatuses.win : betStatuses.lose;
      if (isResultOvered) {
        this.winingBets[betTypes.over] = this.underOverBets[betTypes.over];
        this.winingBets[betTypes.over].winAmount = parseNumberByPrecision(
          this.underOverBets[betTypes.over].amount,
          this.underOverBets[betTypes.over].odd
        );
        winingBalance += this.winingBets[betTypes.over].winAmount;
      } else {
        this.underOverBets[betTypes.over].winAmount = 0;
      }

      // isDemo &&
      //   betStore.updateLiveBets({
      //     userId: undefined,
      //     marketType: betTypes.over,
      //     winAmount: this.underOverBets[betTypes.over].winAmount,
      //     roundId: +this.underOverBets[betTypes.over].roundId,
      //     status: this.underOverBets[betTypes.over].status,
      //     amount: this.underOverBets[betTypes.over].amount,
      //     point: this.underOverBets[betTypes.over].point,
      //     odd: this.underOverBets[betTypes.over].odd,
      //     isDemoBet: true
      //   });
    }

    if (this.underOverBets[betTypes.under]) {
      this.underOverBets[betTypes.under].status = isResultUnder ? betStatuses.win : betStatuses.lose;
      if (isResultUnder) {
        this.winingBets[betTypes.under] = this.underOverBets[betTypes.under];
        this.underOverBets[betTypes.under].winAmount = parseNumberByPrecision(
          this.underOverBets[betTypes.under].amount,
          this.underOverBets[betTypes.under].odd
        );
        winingBalance += this.underOverBets[betTypes.under].winAmount;
      } else {
        this.underOverBets[betTypes.under].winAmount = 0;
      }

      // isDemo &&
      //   betStore.updateLiveBets({
      //     userId: undefined,
      //     marketType: betTypes.under,
      //     winAmount: this.underOverBets[betTypes.under].winAmount,
      //     roundId: +this.underOverBets[betTypes.under].roundId,
      //     status: this.underOverBets[betTypes.under].status,
      //     amount: this.underOverBets[betTypes.under].amount,
      //     point: this.underOverBets[betTypes.under].point,
      //     odd: this.underOverBets[betTypes.under].odd,
      //     isDemoBet: true
      //   });
    }

    // if (isDemo) {
    //   userInfoStore.setDemoBalance(userInfoStore.balance + winingBalance);
    // }
  }

  growChartBy(stepSize) {
    const lastDiff = this.presentPoint.diff;
    const comingDiff = this.presentPoint.diff + stepSize;
    const maxDiffSize = 20;
    this.presentPoint.diff = Math.min(comingDiff, maxDiffSize);
    const lastStepSize = this.presentPoint.diff - lastDiff;
    this.chartStartPoint -= lastStepSize;
    this.presentPoint.x = this.chartStartPoint + this.chartPoints.length * 20 + this.presentPoint.diff;
  }

  shiftAnimationPoint() {
    this.presentPoint.diff = 0;
    this.chartPoints.push(this.animatingPoints.shift());
    this.roundsPoints.push(this.presentPoint.point);
  }

  shiftRound() {
    delete this.betsHistory[Object.keys(this.betsHistory)[0]];
    delete this.roundResults[Object.keys(this.roundResults)[0]];
    this.roundsPoints.splice(0, 5);
    this.chartPoints.splice(0, 5);
    this.chartStartPoint += 100;
  }

  addRoundResult(roundId, result) {
    this.roundResults[roundId] = result;
  }

  setPresentPoint(data) {
    this.presentPoint = data;
  }

  setAnimatingPoints(points) {
    this.animatingPoints = getChartPointsInCanvasCoords(points, this.concentrationDiff);
  }

  setConcentrationPoint(point) {
    this.concentrationPoint = point;
    this.concentrationDiff = getConcentrationDiff(point);
    this.chartPoints = getChartPointsInCanvasCoords(this.roundsPoints, this.concentrationDiff);
    this.presentPoint.y = this.presentPoint.point * 10 - this.concentrationDiff;
  }

  setIsTabVisibility(bool) {
    this.isTabVisibility = bool;
  }

  setLastResult(point) {
    this.lastResult = point;
  }

  doBetForDemo() {
    const { maxBet } = gameConfigStore.limits;
    const clickedBlocks = doBetsStore.betBlocks.filter((block) => block.clicked);
    if (!clickedBlocks.length) {
      return;
    }

    let roundAllBetsAmount = 0;
    clickedBlocks.forEach((block) => (roundAllBetsAmount += block.amount));

    if (roundAllBetsAmount > maxBet) {
      notificationStore.setNotificationType({ responseCode: BetResponseCode.InValidMaxBet });
      return;
    }

    if (userInfoStore.balance - roundAllBetsAmount < 0) {
      notificationStore.setNotificationType({ responseCode: BetResponseCode.InsufficientFunds });
      return;
    }

    clickedBlocks.forEach((block) => {
      const bet = {
        status: 2,
        odd: block.odd,
        roundId: block.roundId,
        amount: block.amount,
        point: block.type === betTypes.range ? block.point : this.presentPoint.point,
        marketType: block.type,
      };

      if (bet.marketType === betTypes.range) {
        this.betsHistory[bet.roundId].push(bet);
      } else {
        this.underOverBets[bet.marketType] = bet;
      }
    });

    this.pushMyDemoBetsInLiveBets();
    userInfoStore.setDemoBalance(userInfoStore.balance - roundAllBetsAmount);
    notificationStore.setNotificationType({ responseCode: BetResponseCode.Success });
  }

  pushMyDemoBetsInLiveBets() {
    const clickedBlocks = doBetsStore.betBlocks.filter((block) => block.clicked);

    clickedBlocks.forEach((bet) => {
      betStore.updateLiveBets({
        userId: +userInfoStore.userId,
        status: betStatuses.pending,
        roundId: +bet.roundId,
        marketType: bet.type,
        amount: bet.amount,
        point: bet.type === betTypes.range ? bet.point : roundsInfoStore.presentPoint.point,
        odd: bet.odd,
        isDemoBet: true,
      });
    });
  }

  setLastWinBets(newLastWinBets) {
    this.lastWinBets = newLastWinBets;
  }

  updateDemoBetHistory() {
    const demoBetHistory = deepCopy(betStore._demoBetHistory);
    const copyBetHistory = deepCopy(this.betsHistory);
    const lastRoundId = Object.keys(copyBetHistory)[6];

    this.betsHistory[lastRoundId].forEach((bet) => demoBetHistory.unshift(createDemoBet(bet)));
    this.underOverBets[betTypes.over] && demoBetHistory.unshift(createDemoBet(this.underOverBets[betTypes.over]));
    this.underOverBets[betTypes.under] && demoBetHistory.unshift(createDemoBet(this.underOverBets[betTypes.under]));

    betStore.setDemoBetHistory(demoBetHistory);
  }
}

export const roundsInfoStore = new RoundsInfoStore();
